import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { labelHobby } from "@/lib/constants";

export default function Apply() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [options, setOptions] = useState({ hobbies: [], incomes: [], regions: [] });
  const [form, setForm] = useState({
    referral_code: "",
    name: "",
    email: "",
    password: "",
    phone: "",
    region: "",
    occupation: "",
    income: "",
    hobbies: [],
  });
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    api.get("/meta/options").then((r) => setOptions(r.data));
  }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleHobby = (h) =>
    setForm((s) => ({
      ...s,
      hobbies: s.hobbies.includes(h) ? s.hobbies.filter((x) => x !== h) : [...s.hobbies, h],
    }));

  const next = () => setStep((s) => Math.min(s + 1, 3));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setErr("");
    setSubmitting(true);
    try {
      await api.post("/auth/register", {
        ...form,
        referral_code: form.referral_code.trim().toUpperCase(),
      });
      setDone(true);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 grain bg-[#0a0a0a]">
        <div className="max-w-lg text-center fade-up">
          <div className="w-16 h-16 mx-auto rounded-full border border-[#d4af37] flex items-center justify-center mb-6">
            <Check size={28} strokeWidth={1.5} className="text-[#d4af37]" />
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl">Application submitted.</h1>
          <p className="mt-6 text-white/60 leading-relaxed">
            Our concierge will review your application within 24–48 hours. You will receive an email at{" "}
            <span className="text-[#d4af37]">{form.email}</span> once a decision is made.
          </p>
          <div className="mt-10 flex gap-4 justify-center">
            <Link to="/" className="text-white/60 hover:text-white text-sm uppercase tracking-[0.2em]" data-testid="back-home-done">
              Return Home
            </Link>
            <Link
              to="/login"
              className="bg-[#d4af37] text-black px-6 py-3 text-sm uppercase tracking-[0.2em] hover:bg-[#f3e5ab] transition"
              data-testid="go-login"
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grain bg-[#0a0a0a]">
      <header className="max-w-3xl mx-auto px-6 sm:px-10 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="apply-back-home">
          <span className="w-8 h-8 rounded-full border border-[#d4af37]/70 flex items-center justify-center">
            <span className="text-[#d4af37] font-serif">L</span>
          </span>
          <span className="font-serif text-xl tracking-wider">Lumière Club</span>
        </Link>
        <div className="text-xs uppercase tracking-[0.3em] text-white/40" data-testid="apply-step-indicator">
          Step {step + 1} / 4
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 sm:px-10 py-8 fade-up">
        {step === 0 && (
          <section data-testid="apply-step-0">
            <p className="text-[#d4af37] text-xs uppercase tracking-[0.3em] mb-3">Begin · 开始</p>
            <h1 className="font-serif text-4xl sm:text-5xl">Who referred you?</h1>
            <p className="text-white/50 mt-3">
              Lumière is invitation-only. Enter the 8-character code given to you by an existing member.
            </p>
            <input
              className="editorial-input mt-10 text-2xl tracking-[0.4em] font-mono uppercase"
              value={form.referral_code}
              onChange={(e) => set("referral_code", e.target.value.toUpperCase().slice(0, 8))}
              placeholder="ABCDEFGH"
              aria-label="Referral code"
              data-testid="apply-referral"
            />
          </section>
        )}

        {step === 1 && (
          <section data-testid="apply-step-1">
            <p className="text-[#d4af37] text-xs uppercase tracking-[0.3em] mb-3">Identity · 身份</p>
            <h1 className="font-serif text-4xl sm:text-5xl">Tell us about yourself.</h1>
            <div className="mt-10 space-y-8">
              <Field label="Full Name · 姓名">
                <input className="editorial-input" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="apply-name" />
              </Field>
              <Field label="Email · 邮箱">
                <input type="email" className="editorial-input" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="apply-email" />
              </Field>
              <Field label="Password · 密码">
                <input
                  type="password"
                  className="editorial-input"
                  value={form.password}
                  onChange={(e) => set("password", e.target.value)}
                  data-testid="apply-password"
                  placeholder="Minimum 6 characters"
                />
              </Field>
              <Field label="WhatsApp · WhatsApp 号码">
                <input
                  className="editorial-input"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  data-testid="apply-phone"
                  placeholder="+60 12 345 6789"
                />
              </Field>
            </div>
          </section>
        )}

        {step === 2 && (
          <section data-testid="apply-step-2">
            <p className="text-[#d4af37] text-xs uppercase tracking-[0.3em] mb-3">Profile · 资料</p>
            <h1 className="font-serif text-4xl sm:text-5xl">Where & what you do.</h1>
            <div className="mt-10 space-y-8">
              <Field label="Malaysian Region · 地区">
                <select
                  className="editorial-input bg-[#0a0a0a]"
                  value={form.region}
                  onChange={(e) => set("region", e.target.value)}
                  data-testid="apply-region"
                >
                  <option value="">Select…</option>
                  {options.regions.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Occupation · 职业">
                <input className="editorial-input" value={form.occupation} onChange={(e) => set("occupation", e.target.value)} data-testid="apply-occupation" />
              </Field>
              <Field label="Monthly Income · 收入">
                <select
                  className="editorial-input bg-[#0a0a0a]"
                  value={form.income}
                  onChange={(e) => set("income", e.target.value)}
                  data-testid="apply-income"
                >
                  <option value="">Select…</option>
                  {options.incomes.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </section>
        )}

        {step === 3 && (
          <section data-testid="apply-step-3">
            <p className="text-[#d4af37] text-xs uppercase tracking-[0.3em] mb-3">Tastes · 爱好</p>
            <h1 className="font-serif text-4xl sm:text-5xl">What stirs you?</h1>
            <p className="text-white/50 mt-3">Pick the lifestyles you enjoy. Choose at least two.</p>
            <div className="mt-10 flex flex-wrap gap-2.5" data-testid="apply-hobbies">
              {options.hobbies.map((h) => (
                <button
                  key={h}
                  type="button"
                  className="hobby-chip rounded-full bg-[#1a1a1a] border border-white/10 text-white/80 px-4 py-2 text-sm"
                  data-selected={form.hobbies.includes(h)}
                  onClick={() => toggleHobby(h)}
                  data-testid={`hobby-chip-${h}`}
                >
                  {labelHobby(h)}
                </button>
              ))}
            </div>
            {err && (
              <p className="mt-8 text-sm text-red-400" data-testid="apply-error">
                {err}
              </p>
            )}
          </section>
        )}

        {/* Consent (only on the final step) */}
        {step === 3 && (
          <label
            className="mt-10 flex items-start gap-3 cursor-pointer text-sm text-white/70 hover:text-white/90 transition select-none"
            data-testid="apply-consent-row"
          >
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#d4af37] shrink-0 cursor-pointer"
              data-testid="apply-consent-checkbox"
            />
            <span className="leading-relaxed">
              我已阅读并同意{" "}
              <Link
                to="/privacy"
                target="_blank"
                rel="noreferrer"
                className="text-[#d4af37] hover:text-[#f3e5ab] underline underline-offset-2"
                data-testid="apply-consent-privacy-link"
              >
                《隐私政策》
              </Link>
              {" "}与{" "}
              <Link
                to="/terms"
                target="_blank"
                rel="noreferrer"
                className="text-[#d4af37] hover:text-[#f3e5ab] underline underline-offset-2"
                data-testid="apply-consent-terms-link"
              >
                《服务条款》
              </Link>
              ，授权 Lumière Club 依此收集和处理我的个人资料。
              <span className="block text-white/40 mt-1 text-xs">
                I have read and agree to the{" "}
                <Link
                  to="/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#d4af37] hover:text-[#f3e5ab] underline underline-offset-2"
                >
                  Privacy Policy
                </Link>
                {" "}and{" "}
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#d4af37] hover:text-[#f3e5ab] underline underline-offset-2"
                >
                  Terms of Service
                </Link>
                .
              </span>
            </span>
          </label>
        )}

        {/* Footer controls */}
        <div className="flex items-center justify-between mt-16">
          <button
            type="button"
            onClick={back}
            disabled={step === 0}
            className="flex items-center gap-2 text-white/50 hover:text-white text-sm uppercase tracking-[0.2em] disabled:opacity-30"
            data-testid="apply-back"
          >
            <ArrowLeft size={16} strokeWidth={1.5} />
            Back
          </button>

          {step < 3 ? (
            <button
              type="button"
              onClick={next}
              disabled={
                (step === 0 && form.referral_code.length < 4) ||
                (step === 1 && (!form.name || !form.email || form.password.length < 6)) ||
                (step === 2 && (!form.region || !form.occupation || !form.income))
              }
              className="flex items-center gap-2 bg-[#d4af37] text-black px-6 py-3 text-sm uppercase tracking-[0.2em] hover:bg-[#f3e5ab] transition disabled:opacity-40"
              data-testid="apply-next"
            >
              Continue
              <ArrowRight size={16} strokeWidth={2} />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={submitting || form.hobbies.length < 2 || !consent}
              className="flex items-center gap-2 bg-[#d4af37] text-black px-8 py-3 text-sm uppercase tracking-[0.2em] hover:bg-[#f3e5ab] transition disabled:opacity-40"
              data-testid="apply-submit"
            >
              {submitting ? "Submitting…" : "Submit Application"}
            </button>
          )}
        </div>
      </div>

      <div className="mt-12 max-w-2xl mx-auto px-6 sm:px-10 text-center flex justify-center gap-6">
        <Link
          to="/privacy"
          className="text-[10px] uppercase tracking-[0.25em] text-white/30 hover:text-[#d4af37] transition"
          data-testid="apply-privacy-link"
        >
          Privacy · 隐私政策
        </Link>
        <Link
          to="/terms"
          className="text-[10px] uppercase tracking-[0.25em] text-white/30 hover:text-[#d4af37] transition"
          data-testid="apply-terms-link"
        >
          Terms · 服务条款
        </Link>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] text-white/40">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
